import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import {
  Container,
  Typography,
  Stack,
  Box,
  Paper,
  Badge,
  CssBaseline,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  TextField,
  Button,
  Collapse,
  CircularProgress
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import SettingsIcon from "@mui/icons-material/Settings";
import MailIcon from "@mui/icons-material/Mail";
import PeopleIcon from "@mui/icons-material/People";
import GroupsIcon from "@mui/icons-material/Groups";
import ArticleIcon from "@mui/icons-material/Article";
import LogoutIcon from "@mui/icons-material/Logout";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EventIcon from "@mui/icons-material/Event";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { supabase } from "./supabaseClient";
import { useGroupQuery, useUpdateLeaderMessage, useUserMembershipsQuery } from "./hooks/useGroups";
import { useUnreadRequestCount } from "./hooks/useGroupRequests";
import { useDisplayNameQuery } from "./hooks/useUsers";
import * as groupRequestsService from "./services/groupRequestsService";
import * as usersService from "./services/usersService";
import * as nameProfilesService from "./services/nameProfilesService";
import "./App.css";

import UserPrompt from "./components/UserPrompt";
import NamesList from "./components/NamesList";
import NameDetails from "./components/NameDetails";
import ClaimedItems from "./components/ClaimedItems";
import ProfilePicker from "./components/ProfilePicker";
import AdminUsers from "./components/AdminUsers";
import Settings from "./components/Settings";
import GroupSelection from "./components/GroupSelection";
import AdminGroups from "./components/AdminGroups";
import Messages from "./components/Messages";
import AdminLogs from "./components/AdminLogs";
import LeaderActions from "./components/LeaderActions";
import Help from "./components/Help";
import About from "./components/About";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import GiftBackground from "./components/GiftBackground";
import GroupSwitcherSheet from "./components/GroupSwitcherSheet";

import logoCropped from "./assets/wishlist-manager-logo-cropped.png";
import { setLogUser, logger } from "./utils/logger";
import { getCountdownDisplay, getNextChristmas, isWishlistLocked } from "./utils/countdown";

const VIEW_LABELS = {
  names:         'Wishlists',
  claimed:       'Shopping List',
  settings:      'Settings',
  profile:       'My Profile',
  messages:      'Messages',
  admin:         'Admin Users',
  adminGroups:   'Admin Groups',
  adminLogs:     'Admin Logs',
  leaderActions: 'Group Leader Actions',
  help:          'User Guide',
  about:         'About',
  terms:         'Terms of Service',
  privacy:       'Privacy Policy',
};

function App() {
  const [userId, setUserId] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [view, setView] = useState("names");
  const [selectedNameId, setSelectedNameId] = useState(null);

  // user settings (per user)
  const [fontScale, setFontScale] = useState(1.0);
  const [isParent, setIsParent] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // group state
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [primaryGroupId, setPrimaryGroupId] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const [editingLeaderMessage, setEditingLeaderMessage] = useState(false);
  const [leaderMessageDraft, setLeaderMessageDraft] = useState("");
  const [leaderMessageExpanded, setLeaderMessageExpanded] = useState(true);
  const [showGroupSelection, setShowGroupSelection] = useState(false);
  const [isPendingApproval, setIsPendingApproval] = useState(false);
  const [sessionAwaitingProfile, setSessionAwaitingProfile] = useState(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const approvalChannelRef = useRef(null);

  // nav drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  const theme = useMemo(() => {
    const baseFontSize = 14 * (Number(fontScale) || 1.0);

    return createTheme({
      palette: {
        mode: "dark",
        primary: { main: "#B71C1C" },
        secondary: { main: "#0B3D2E" },
        background: {
          default: "#0F2C24",
          paper: "#122E25"
        },
        text: {
          primary: "#F8F3E3"
        }
      },
      typography: {
        fontSize: baseFontSize,
        fontFamily: "Inter, Roboto, 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
      },
      shape: {
        borderRadius: 18
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 999,
              textTransform: "none",
              boxShadow: "none"
            }
          }
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              borderRadius: 24
            }
          }
        }
      }
    });
  }, [fontScale]);

  const applySettings = useCallback((s) => {
    setFontScale(typeof s?.font_scale === "number" ? s.font_scale : 1.0);
    setIsParent(!!s?.is_parent);
    // role is only present on the initial login profile load (Settings.js's
    // save callback never includes it) — skip when absent so saving an
    // unrelated setting doesn't wrongly clear the caller's own admin status.
    if (typeof s?.role === "string") {
      setIsAdmin(s.role === "admin");
    }
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(tick);
  }, []);

  const { data: activeGroup } = useGroupQuery(activeGroupId);
  const { data: unreadCount = 0 } = useUnreadRequestCount({ userId, isAdmin });
  const activeGroupName = activeGroup?.name ?? null;
  const groupLeaderId = activeGroup?.leader_id ?? null;
  const groupLeaderMessage = activeGroup?.leader_message ?? null;
  const wishlistDueDate = activeGroup?.wishlist_due_date ?? null;
  const giftExchangeDate = activeGroup?.gift_exchange_date ?? null;
  const wishlistUnlockedOverride = !!activeGroup?.wishlist_unlocked_override;
  const groupJoinCode = activeGroup?.join_code ?? null;
  const { data: groupLeaderName } = useDisplayNameQuery(activeGroup?.leader_id);
  const { data: userMemberships = [] } = useUserMembershipsQuery(userId);

  const isGroupLeader = !!userId && !!activeGroupId && groupLeaderId === userId;
  const wishlistLocked = isWishlistLocked(wishlistDueDate, wishlistUnlockedOverride, now);

  const countdownRibbons = [
    wishlistDueDate && { key: "due", emoji: "🎁", label: "Due", ...getCountdownDisplay(wishlistDueDate, now) },
    giftExchangeDate && { key: "exchange", emoji: "🥂", label: "Exchange", ...getCountdownDisplay(giftExchangeDate, now) },
    { key: "christmas", emoji: "🎅", label: "Christmas", ...getCountdownDisplay(getNextChristmas(now), now) },
  ].filter((c) => c && c.status !== "expired");

  const startEditingLeaderMessage = () => {
    setLeaderMessageDraft(groupLeaderMessage ?? "");
    setEditingLeaderMessage(true);
  };

  const cancelEditingLeaderMessage = () => {
    setLeaderMessageDraft(groupLeaderMessage ?? "");
    setEditingLeaderMessage(false);
  };

  const handleGroupSwitch = (groupId) => {
    setActiveGroupId(groupId);
    logger.info('GroupSwitcherSheet', 'active_group_changed', { groupId });
  };

  const updateLeaderMessageMutation = useUpdateLeaderMessage();

  const saveLeaderMessage = async () => {
    const trimmed = leaderMessageDraft.trim();
    try {
      await updateLeaderMessageMutation.mutateAsync({ groupId: activeGroupId, message: trimmed });
      setEditingLeaderMessage(false);
    } catch {
      // Error already logged in groupsService; leave the editor open so the user can retry.
    }
  };

  useEffect(() => {
    if (!userId || !leaderMessageExpanded) return;
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setLeaderMessageExpanded(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [userId, leaderMessageExpanded]);

  useEffect(() => {
    const handlePopState = (e) => {
      const state = e.state ?? {};
      setView(state.view ?? "names");
      setSelectedNameId(state.selectedNameId ?? null);
      setDrawerOpen(false);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const subscribeToApproval = useCallback((id) => {
    const channel = supabase
      .channel(`user-approval-${id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "users", filter: `user_id=eq.${id}` },
        (payload) => {
          if (payload.new.primary_group_id) {
            setPrimaryGroupId(payload.new.primary_group_id);
            setActiveGroupId(payload.new.primary_group_id);
            setIsPendingApproval(false);
            channel.unsubscribe();
            approvalChannelRef.current = null;
            logger.info('App', 'group_approval_received', { userId: id, groupId: payload.new.primary_group_id });
          }
        }
      )
      .subscribe();
    approvalChannelRef.current = channel;
  }, []);

  const loadUserProfile = useCallback(async (id) => {
    setUserId(id);
    setLogUser(id);

    // Fire-and-forget: if this session carries a signup date of birth in auth
    // metadata, write it through to the private name_profiles.birthdate once,
    // then strip the raw date back out of auth metadata (data minimization —
    // nothing reads that copy after the write-through). Never awaited — login
    // latency must be unchanged. The strip runs only once setBirthdateAtSignup
    // resolves: any resolve (inserted / updated / already-set / benign race)
    // means name_profiles now carries the date; a throw means "leave it".
    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        const bd = session?.user?.user_metadata?.birthdate;
        if (!bd) return;
        await nameProfilesService.setBirthdateAtSignup({ userId: id, birthdate: bd });
        await nameProfilesService.clearSignupBirthdate();
      })
      .catch(() => logger.warn("App", "signup_birthdate_writethrough_failed", { userId: id }));

    const profile = await usersService.getUserProfile(id);

    applySettings(profile);

    if (profile?.primary_group_id) {
      setPrimaryGroupId(profile.primary_group_id);
      setActiveGroupId(profile.primary_group_id);
    } else {
      const hasPending = await groupRequestsService.hasPendingGroupRequest({ userId: id });

      if (hasPending) {
        setIsPendingApproval(true);
        subscribeToApproval(id);
      } else {
        setShowGroupSelection(true);
      }
    }
  }, [applySettings, subscribeToApproval]);

  const handleUserSet = useCallback(async (id) => {
    setIsPasswordRecovery(false);
    await loadUserProfile(id);
    logger.info('App', 'login_success', { userId: id });
    window.history.replaceState({ view: "names", selectedNameId: null }, "");
    setView("names");
    setSelectedNameId(null);
    setLeaderMessageExpanded(true);
  }, [loadUserProfile]);

  // Fires when the user lands via a Supabase password-recovery link. Must be
  // handled distinctly from a normal restored session below, since a recovery
  // link also produces a valid session — without this flag, a returning user's
  // session-restore would log them straight into the app instead of prompting
  // for a new password.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsPasswordRecovery(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Branded Auth email templates link back to our own domain with a
      // token_hash instead of Supabase's hosted /auth/v1/verify redirect
      // (avoids a Resend "link domain mismatch" warning) — so recovery,
      // invite, and signup-confirmation links now need to be exchanged for
      // a session here.
      const params = new URLSearchParams(window.location.search);
      const tokenHash = params.get("token_hash");
      const otpType = params.get("type");
      if (tokenHash && (otpType === "recovery" || otpType === "invite" || otpType === "signup")) {
        const { error: verifyError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: otpType });
        if (verifyError) {
          logger.warn('App', 'verify_otp_failed', { type: otpType, error: verifyError.message });
        }
        window.history.replaceState({}, "", window.location.pathname);
      }

      const { data: { session } } = await supabase.auth.getSession();
      const authId = session?.user?.id;

      if (authId) {
        const userId = await usersService.getUserIdByAuthId(authId);

        if (!cancelled && userId) {
          await loadUserProfile(userId);
          if (!cancelled) {
            logger.info('App', 'session_restored', { userId });
            const restoredState = window.history.state;
            setView(restoredState?.view ?? "names");
            setSelectedNameId(restoredState?.selectedNameId ?? null);
            setLeaderMessageExpanded(false);
          }
        } else if (!cancelled) {
          // Authenticated (e.g. via an invite-email link) but no linked profile yet —
          // route into UserPrompt's profile-creation screen instead of the sign-in form.
          setSessionAwaitingProfile({ invitedGroupId: session.user.app_metadata?.invited_group_id ?? null });
        }
      }

      if (!cancelled) setAuthLoading(false);
    })();

    return () => { cancelled = true; };
  }, [loadUserProfile]);

  const handleExitToLogin = useCallback(() => {
    setSessionAwaitingProfile(null);
    setIsPasswordRecovery(false);
  }, []);

  const handleSignOut = async (skipServerSignOut = false) => {
    await logger.info('App', 'sign_out', { userId });
    if (!skipServerSignOut) {
      try {
        await supabase.auth.signOut();
      } catch (e) {
        console.error("Sign out error", e);
      }
    }

    setUserId(null);
    setLogUser(null);
    window.history.replaceState({}, "");
    setView("names");
    setSelectedNameId(null);

    // reset to defaults for next login prompt
    setFontScale(1.0);
    setIsParent(false);
    setIsAdmin(false);

    // reset group state
    setActiveGroupId(null);
    setPrimaryGroupId(null);
    setShowGroupSelection(false);
    setIsPendingApproval(false);
    setSessionAwaitingProfile(null);
    setDrawerOpen(false);
    approvalChannelRef.current?.unsubscribe();
    approvalChannelRef.current = null;
  };

  const navigate = (newView) => {
    window.history.pushState({ view: newView, selectedNameId: null }, "");
    setView(newView);
    setSelectedNameId(null);
    setDrawerOpen(false);
    setLeaderMessageExpanded(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className="app-shell" data-theme="dark" sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {userId && !isPasswordRecovery && <GiftBackground />}

        {userId && !showGroupSelection && !isPasswordRecovery && (
          <>
            <AppBar position="sticky" color="primary" elevation={4} sx={{ bgcolor: 'primary.dark' }}>
              <Toolbar>
                <IconButton color="inherit" edge="start" onClick={() => setDrawerOpen(true)} sx={{ mr: 1 }}>
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6" sx={{ flexGrow: 1, textAlign: "center", fontWeight: 700 }}>
                  {VIEW_LABELS[view] ?? 'Wishlists'}
                </Typography>
              </Toolbar>
            </AppBar>

            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
              <Box sx={{ width: 240 }} role="presentation">
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight={700}>Menu</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                    Logged in as <strong>{userId}</strong>
                  </Typography>
                </Box>
                <Divider />
                <List>
                  <ListItemButton selected={view === "names"} onClick={() => navigate("names")}>
                    <ListItemIcon><HomeIcon /></ListItemIcon>
                    <ListItemText primary="Wishlists" />
                  </ListItemButton>
                  <ListItemButton selected={view === "claimed"} onClick={() => navigate("claimed")}>
                    <ListItemIcon><CardGiftcardIcon /></ListItemIcon>
                    <ListItemText primary="Shopping List" />
                  </ListItemButton>
                  <ListItemButton selected={view === "settings"} onClick={() => navigate("settings")}>
                    <ListItemIcon><SettingsIcon /></ListItemIcon>
                    <ListItemText primary="Settings" />
                  </ListItemButton>
                  <ListItemButton selected={view === "profile"} onClick={() => navigate("profile")}>
                    <ListItemIcon><PersonOutlineIcon /></ListItemIcon>
                    <ListItemText primary="My Profile" />
                  </ListItemButton>
                  <ListItemButton selected={view === "messages"} onClick={() => navigate("messages")}>
                    <ListItemIcon>
                      <Badge badgeContent={unreadCount} color="error">
                        <MailIcon />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText primary="Messages" />
                  </ListItemButton>
                  <ListItemButton selected={view === "help"} onClick={() => navigate("help")}>
                    <ListItemIcon><HelpOutlineIcon /></ListItemIcon>
                    <ListItemText primary="User Guide" />
                  </ListItemButton>
                </List>
                {isGroupLeader && (
                  <>
                    <Divider />
                    <List>
                      <ListItemButton selected={view === "leaderActions"} onClick={() => navigate("leaderActions")}>
                        <ListItemIcon><EventIcon /></ListItemIcon>
                        <ListItemText primary="Group Leader Actions" />
                      </ListItemButton>
                    </List>
                  </>
                )}
                {isAdmin && (
                  <>
                    <Divider />
                    <List>
                      <ListItemButton selected={view === "admin"} onClick={() => navigate("admin")}>
                        <ListItemIcon><PeopleIcon /></ListItemIcon>
                        <ListItemText primary="Admin Users" />
                      </ListItemButton>
                      <ListItemButton selected={view === "adminGroups"} onClick={() => navigate("adminGroups")}>
                        <ListItemIcon><GroupsIcon /></ListItemIcon>
                        <ListItemText primary="Admin Groups" />
                      </ListItemButton>
                      <ListItemButton selected={view === "adminLogs"} onClick={() => navigate("adminLogs")}>
                        <ListItemIcon><ArticleIcon /></ListItemIcon>
                        <ListItemText primary="Admin Logs" />
                      </ListItemButton>
                    </List>
                  </>
                )}
                <Divider />
                <List>
                  <ListItemButton selected={view === "about"} onClick={() => navigate("about")}>
                    <ListItemIcon><InfoIcon /></ListItemIcon>
                    <ListItemText primary="About" />
                  </ListItemButton>
                  <ListItemButton onClick={() => { setDrawerOpen(false); handleSignOut(); }}>
                    <ListItemIcon><LogoutIcon /></ListItemIcon>
                    <ListItemText primary="Sign Out" />
                  </ListItemButton>
                </List>
              </Box>
            </Drawer>
          </>
        )}

        <Container className="app-container" maxWidth="lg" sx={{ py: 2 }}>
          {authLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
              <CircularProgress color="primary" />
            </Box>
          )}

          {!authLoading && isPasswordRecovery && (
            <UserPrompt onUserSet={handleUserSet} passwordRecovery />
          )}

          {!authLoading && !userId && !isPasswordRecovery && (
            <UserPrompt
              onUserSet={handleUserSet}
              sessionAwaitingProfile={sessionAwaitingProfile}
              onExitToLogin={handleExitToLogin}
            />
          )}

          {userId && showGroupSelection && !isPasswordRecovery && (
            <GroupSelection
              userId={userId}
              onComplete={({ isParent: chosenIsParent, joinedGroupId }) => {
                setShowGroupSelection(false);
                setIsParent(!!chosenIsParent);
                if (joinedGroupId) {
                  setPrimaryGroupId(joinedGroupId);
                  setActiveGroupId(joinedGroupId);
                } else {
                  setIsPendingApproval(true);
                  subscribeToApproval(userId);
                }
              }}
            />
          )}

          {userId && !showGroupSelection && !isPasswordRecovery && (
            <>
              <Paper elevation={0} className="hero-card" sx={{ mb: 4, p: { xs: 2, md: 4 }, bgcolor: 'rgba(255, 44, 44, 0.28)' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={{ xs: 1.5, md: 3 }}
                >
                  <Box sx={{ display: "flex", justifyContent: "center", flexShrink: 0, width: { xs: "auto", md: 320 }, ml: { md: 4 } }}>
                    <Box
                      component="img"
                      src={logoCropped}
                      alt="Wishlist Manager"
                      sx={{ width: { xs: 80, sm: 120, md: 220 }, height: "auto" }}
                    />
                  </Box>

                  <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <GroupSwitcherSheet
                        activeGroupId={activeGroupId}
                        activeGroupName={activeGroupName}
                        primaryGroupId={primaryGroupId}
                        memberships={userMemberships}
                        onSwitch={handleGroupSwitch}
                      />
                      {countdownRibbons.length > 0 && (
                        <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
                          {countdownRibbons.map((c) => (
                            <Box
                              key={c.key}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                bgcolor: "rgba(0,0,0,0.22)",
                                border: "1px solid",
                                borderColor: "rgba(244,239,228,0.14)",
                                borderRadius: 999,
                                px: 1.25,
                                py: 0.5,
                              }}
                            >
                              <Typography sx={{ fontSize: "0.9rem" }}>{c.emoji}</Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.03em" }}
                              >
                                {c.label}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: "warning.light", ml: "auto" }}
                              >
                                {c.text}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                    {(isGroupLeader || groupLeaderMessage) && (
                      <Stack spacing={0.5} sx={{ maxWidth: 600 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{ cursor: "pointer" }}
                          onClick={() => setLeaderMessageExpanded((prev) => !prev)}
                          aria-expanded={leaderMessageExpanded}
                        >
                          <Typography variant="subtitle2" sx={{ color: "primary.main", fontWeight: 700 }}>
                            Message from {groupLeaderName}
                          </Typography>
                          <IconButton
                            size="small"
                            sx={{ color: "primary.main" }}
                            aria-label={leaderMessageExpanded ? "Collapse message" : "Expand message"}
                          >
                            {leaderMessageExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Stack>

                        <Collapse in={leaderMessageExpanded}>
                          <Stack spacing={0.5}>
                            {isGroupLeader && (editingLeaderMessage || !groupLeaderMessage) && (
                              <Stack spacing={1}>
                                <TextField
                                  multiline
                                  minRows={2}
                                  fullWidth
                                  value={leaderMessageDraft}
                                  onChange={(e) => setLeaderMessageDraft(e.target.value)}
                                  placeholder="Let your group know the wishlist deadline, the gift-exchange date/time/location, and how to reach you with questions..."
                                />
                                <Stack direction="row" spacing={2}>
                                  <Button variant="contained" onClick={saveLeaderMessage}>Save</Button>
                                  <Button variant="outlined" onClick={cancelEditingLeaderMessage}>Cancel</Button>
                                </Stack>
                              </Stack>
                            )}

                            {isGroupLeader && !editingLeaderMessage && groupLeaderMessage && (
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                                  {groupLeaderMessage}
                                </Typography>
                                <IconButton size="small" onClick={startEditingLeaderMessage}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            )}

                            {!isGroupLeader && groupLeaderMessage && (
                              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                                {groupLeaderMessage}
                              </Typography>
                            )}
                          </Stack>
                        </Collapse>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Paper>

              {view === "names" && !selectedNameId && (
                <NamesList
                  userId={userId}
                  onSelectName={(id) => {
                    window.history.pushState({ view: "names", selectedNameId: id }, "");
                    setSelectedNameId(id);
                    setLeaderMessageExpanded(false);
                  }}
                  activeGroupId={activeGroupId}
                  isPendingApproval={isPendingApproval}
                  isParent={isParent}
                />
              )}

              {selectedNameId && (
                <NameDetails
                  nameId={selectedNameId}
                  userId={userId}
                  isParent={isParent}
                  isWishlistLocked={wishlistLocked}
                  onNavigateHome={() => {
                    setSelectedNameId(null);
                    setLeaderMessageExpanded(false);
                  }}
                />
              )}

              {view === "claimed" && <ClaimedItems userId={userId} activeGroupId={activeGroupId} onNavigateHome={() => navigate("names")} />}

              {view === "profile" && (
                <ProfilePicker
                  userId={userId}
                  activeGroupId={activeGroupId}
                  isParent={isParent}
                  onNavigateHome={() => navigate("names")}
                />
              )}

              {view === "settings" && (
                <Settings
                  userId={userId}
                  onSettingsChange={applySettings}
                  activeGroupId={activeGroupId}
                  setActiveGroupId={setActiveGroupId}
                  primaryGroupId={primaryGroupId}
                  onPrimaryGroupChange={(groupId) => {
                    setPrimaryGroupId(groupId);
                    setActiveGroupId(groupId);
                  }}
                  onNavigateToMessages={() => navigate("messages")}
                  onNavigateHome={() => navigate("names")}
                  onAccountDeleted={() => handleSignOut(true)}
                />
              )}

              {view === "admin" && isAdmin && (
                <AdminUsers currentUserId={userId} onNavigateHome={() => navigate("names")} />
              )}

              {view === "messages" && (
                <Messages
                  userId={userId}
                  isAdmin={isAdmin}
                  onNavigateHome={() => navigate("names")}
                />
              )}

              {view === "adminGroups" && isAdmin && (
                <AdminGroups currentUserId={userId} onNavigateHome={() => navigate("names")} />
              )}

              {view === "adminLogs" && isAdmin && (
                <AdminLogs onNavigateHome={() => navigate("names")} />
              )}

              {view === "leaderActions" && isGroupLeader && (
                <LeaderActions
                  activeGroupId={activeGroupId}
                  userId={userId}
                  wishlistDueDate={wishlistDueDate}
                  giftExchangeDate={giftExchangeDate}
                  wishlistUnlockedOverride={wishlistUnlockedOverride}
                  joinCode={groupJoinCode}
                  onNavigateHome={() => navigate("names")}
                />
              )}

              {view === "help" && <Help onNavigateHome={() => navigate("names")} />}
              {view === "about" && <About onNavigateHome={() => navigate("names")} onNavigate={navigate} />}
              {view === "terms" && <TermsOfService onNavigateHome={() => navigate("names")} onNavigate={navigate} />}
              {view === "privacy" && <PrivacyPolicy onNavigateHome={() => navigate("names")} onNavigate={navigate} />}
            </>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
